import React, { useState } from 'react';
import { Upload, Download, FileSpreadsheet, CheckCircle2, AlertTriangle, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { previewLeadImport, importLeadsFile } from '../lib/leadService';
import { formatApiError } from '../lib/campaignService';

const MAX_UPLOAD_MB = 4;

function uploadError(err, fallback) {
  if (err?.response?.status === 413) {
    return `File is too large for upload (max ~${MAX_UPLOAD_MB} MB). Split it into smaller files.`;
  }
  return formatApiError(err, fallback);
}

function csvCell(value) {
  const text = String(value ?? '');
  return /[",\n\r]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
}

function downloadRejected(rows) {
  const lines = [['Row', 'Reason', 'Value', 'Name'].join(',')];
  rows.forEach((r) => lines.push([r.row, r.reason, r.value, r.name].map(csvCell).join(',')));
  const blob = new Blob([lines.join('\r\n')], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'rejected-rows.csv';
  a.click();
  URL.revokeObjectURL(url);
}

function Stat({ label, value, tone }) {
  return (
    <div className={`lead-import-stat${tone ? ` ${tone}` : ''}`}>
      <span className="lead-import-stat-value">{value}</span>
      <span className="lead-import-stat-label">{label}</span>
    </div>
  );
}

export default function LeadImportTab({ apiUrl }) {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [mapping, setMapping] = useState({});
  const [checking, setChecking] = useState(false);
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);

  const runPreview = async (f, mapOverride) => {
    setChecking(true);
    try {
      const data = await previewLeadImport(apiUrl, f, mapOverride);
      setPreview(data);
      setMapping(data.mapping || {});
    } catch (err) {
      setPreview(null);
      toast.error(uploadError(err, 'Could not read the file'));
    } finally {
      setChecking(false);
    }
  };

  const handleFile = (e) => {
    const f = e.target.files?.[0] || null;
    e.target.value = '';
    setResult(null);
    setPreview(null);
    setFile(f);
    if (f) runPreview(f, null);
  };

  const handleMappingChange = (fieldName, header) => {
    const next = { ...mapping };
    if (header) next[fieldName] = header; else delete next[fieldName];
    setMapping(next);
    runPreview(file, next);
  };

  const handleImport = async () => {
    if (!file || !preview) return;
    setImporting(true);
    try {
      const data = await importLeadsFile(apiUrl, file, mapping);
      setResult(data);
      setPreview(null);
      setFile(null);
      toast.success(`Import done: ${data.imported} new, ${data.updated} updated`);
    } catch (err) {
      toast.error(uploadError(err, 'Lead import failed'));
    } finally {
      setImporting(false);
    }
  };

  const fieldOrder = preview ? Object.keys(preview.field_labels || {}) : [];

  return (
    <div className="lead-import-page">
      <div className="section-header">
        <div>
          <h2><FileSpreadsheet size={22} /> Import Leads</h2>
          <p className="lead-import-hint">
            Upload a CSV or Excel export from your CRM. Numbers are cleaned and matched on the mobile
            number, so nobody is added twice.
          </p>
        </div>
      </div>

      <div className="lead-import-panel">
        <div className="campaign-import-actions">
          <label className="catalog-upload-btn">
            <Upload size={16} />
            {file ? file.name : 'Choose .csv or .xlsx file'}
            <input
              type="file"
              accept=".csv,.xlsx,.xlsm,text/csv"
              style={{ display: 'none' }}
              onChange={handleFile}
              disabled={checking || importing}
            />
          </label>
          <a className="campaign-secondary-btn" href="/samples/leads_sample.csv" download>
            <Download size={16} /> Download sample CSV
          </a>
        </div>
        <p className="lead-import-hint">
          Only <strong>mobile_number</strong> is required. Other columns (name, business_name, city, zone,
          category, source, lead_status, email, address…) are optional. Files up to ~{MAX_UPLOAD_MB} MB.
        </p>
        {checking && <p className="lead-import-hint"><RefreshCw size={14} /> Checking file…</p>}
      </div>

      {preview && (
        <div className="lead-import-panel">
          <h3>Check before importing</h3>
          <div className="lead-import-stats">
            <Stat label="Rows in file" value={preview.total_rows} />
            <Stat label="Unique numbers" value={preview.unique_numbers} />
            <Stat label="New leads" value={preview.new_leads} tone="good" />
            <Stat label="Already in Leads (will update)" value={preview.already_in_leads} />
            <Stat label="Repeated numbers merged" value={preview.duplicates_in_file} />
            <Stat label="Landlines (kept, no WhatsApp)" value={preview.non_whatsapp} tone={preview.non_whatsapp ? 'warn' : ''} />
            <Stat label="Rejected rows" value={preview.rejected_total} tone={preview.rejected_total ? 'bad' : ''} />
          </div>

          <ul className="lead-import-notes">
            <li>
              Rows that share a mobile number become <strong>one lead</strong>; the row with the latest date
              is used and the other rows are kept in the lead&apos;s history.
            </li>
            {preview.repaired > 0 && <li>{preview.repaired} number(s) were repaired (extra 0 removed, several numbers in one cell…).</li>}
            {preview.non_whatsapp > 0 && <li>Landline numbers are saved as leads but are left out of WhatsApp campaigns.</li>}
            <li>Existing leads keep their bot status (new / contacted / closed).</li>
          </ul>

          {preview.shared_numbers?.length > 0 && (
            <div className="lead-import-warning">
              <AlertTriangle size={16} />
              <div>
                <strong>Numbers used by many different contacts</strong> — probably a placeholder or an
                employee&apos;s own number. Check these in your CRM:
                <ul>
                  {preview.shared_numbers.map((s) => (
                    <li key={s.phone}>+{s.phone} — {s.rows} rows, {s.contacts} different contacts</li>
                  ))}
                </ul>
              </div>
            </div>
          )}

          <details className="lead-import-mapping" open={preview.phone_column_guessed}>
            <summary>
              Columns detected{preview.phone_column_guessed ? ' — please confirm the phone column' : ''}
            </summary>
            <div className="lead-import-mapping-grid">
              {fieldOrder.map((fieldName) => (
                <label key={fieldName}>
                  <span>{preview.field_labels[fieldName]}{fieldName === 'phone' ? ' *' : ''}</span>
                  <select
                    value={mapping[fieldName] || ''}
                    onChange={(e) => handleMappingChange(fieldName, e.target.value)}
                    disabled={checking}
                  >
                    <option value="">— not used —</option>
                    {preview.headers.filter(Boolean).map((h) => (
                      <option key={h} value={h}>{h}</option>
                    ))}
                  </select>
                </label>
              ))}
            </div>
          </details>

          {preview.sample?.length > 0 && (
            <div className="lead-import-table-wrap">
              <table className="lead-import-table">
                <thead>
                  <tr><th>Row</th><th>Name</th><th>Phone in file</th><th>Cleaned</th><th>City</th><th>Type</th></tr>
                </thead>
                <tbody>
                  {preview.sample.map((r) => (
                    <tr key={r.row} className={r.phone_ok ? '' : 'bad'}>
                      <td>{r.row}</td>
                      <td>{r.name || r.business_name || '—'}</td>
                      <td>{r.phone || '—'}</td>
                      <td>{r.phone_ok ? `+${r.phone_normalized}` : 'invalid'}</td>
                      <td>{r.city || '—'}</td>
                      <td>{r.phone_type || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <small>First {preview.sample.length} rows.</small>
            </div>
          )}

          {preview.rejected_total > 0 && (
            <div className="lead-import-table-wrap">
              <h4>
                Rejected rows ({preview.rejected_total})
                <button type="button" className="campaign-secondary-btn" onClick={() => downloadRejected(preview.rejected)}>
                  <Download size={14} /> Download{preview.rejected_total > preview.rejected.length ? ` first ${preview.rejected.length}` : ''}
                </button>
              </h4>
              <table className="lead-import-table">
                <thead><tr><th>Row</th><th>Value</th><th>Name</th><th>Why</th></tr></thead>
                <tbody>
                  {preview.rejected.slice(0, 15).map((r) => (
                    <tr key={r.row}><td>{r.row}</td><td>{r.value || '—'}</td><td>{r.name || '—'}</td><td>{r.reason}</td></tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div className="campaign-import-actions">
            <button
              type="button"
              className="campaign-primary-btn"
              disabled={importing || checking || preview.valid_rows === 0 || !mapping.phone}
              onClick={handleImport}
            >
              {importing ? 'Importing…' : `Import ${preview.unique_numbers} lead${preview.unique_numbers === 1 ? '' : 's'}`}
            </button>
            <span className="lead-import-hint">
              {preview.new_leads} new, {preview.already_in_leads} will be updated.
            </span>
          </div>
        </div>
      )}

      {result && (
        <div className="lead-import-panel">
          <h3><CheckCircle2 size={18} /> Import finished</h3>
          <div className="lead-import-stats">
            <Stat label="New leads" value={result.imported} tone="good" />
            <Stat label="Updated" value={result.updated} />
            <Stat label="Repeated numbers merged" value={result.duplicates_in_file} />
            <Stat label="Landlines (no WhatsApp)" value={result.non_whatsapp} />
            <Stat label="Rejected rows" value={result.rejected_total} tone={result.rejected_total ? 'bad' : ''} />
            <Stat label="Failed to save" value={result.failed} tone={result.failed ? 'bad' : ''} />
          </div>
          {result.rejected_total > 0 && (
            <button type="button" className="campaign-secondary-btn" onClick={() => downloadRejected(result.rejected)}>
              <Download size={14} /> Download rejected rows
            </button>
          )}
        </div>
      )}
    </div>
  );
}
